import { geocodeAddresses } from './api';
import { getGeoEntries, putGeoEntries } from './db';
import type { Reading } from './types';

export interface GeoPoint { lat: number; lon: number }

const FOUND_MAX_AGE_MS = 365 * 24 * 3600 * 1000;
// kleine Pakete: bei Nominatim (eine Anfrage pro Sekunde) dauert ein Paket etwa 20 Sekunden
const CHUNK = 20;
const MISSING_MAX_AGE_MS = 7 * 24 * 3600 * 1000;

/** Schlüssel einer Adresse (Straße und Ort, ohne Groß-/Kleinschreibung). */
export function addressKey(item: Pick<Reading, 'Strasse' | 'Ort'>): string {
  return `${(item.Strasse ?? '').trim()}|${(item.Ort ?? '').trim()}`.toLowerCase();
}

/** Entfernung in Kilometern (Haversine). */
export function distanceKm(a: GeoPoint, b: GeoPoint): number {
  const rad = (degrees: number) => degrees * Math.PI / 180;
  const h = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lon - a.lon) / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function formatDistance(km: number): string {
  if (km < 1) return `${Math.max(10, Math.round(km * 100) * 10)} m`;
  return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: km < 10 ? 1 : 0 }).format(km)} km`;
}

/** Aktueller Standort des Geräts (GPS, WLAN oder Mobilfunk). */
export function getPosition(): Promise<GeoPoint> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) { reject(new Error('Dieses Gerät unterstützt keine Standortermittlung.')); return; }
    navigator.geolocation.getCurrentPosition(
      position => resolve({ lat: position.coords.latitude, lon: position.coords.longitude }),
      error => reject(new Error(
        error.code === error.PERMISSION_DENIED ? 'Der Standortzugriff wurde nicht erlaubt. Bitte den Standort für diese Seite in den Browser- oder Geräteeinstellungen freigeben.'
          : error.code === error.TIMEOUT ? 'Die Standortermittlung hat zu lange gedauert. Bitte erneut versuchen.'
            : 'Der Standort ist nicht verfügbar (kein GPS-Signal?).')),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  });
}

/**
 * Koordinaten der Adressen: zuerst aus dem Speicher des Geräts, fehlende bei Azure Maps (über die Function).
 * Offline bleiben fehlende Adressen ohne Koordinate. `onProgress` meldet, wie viele Adressen noch abgefragt werden.
 */
export async function resolveCoordinates(
  items: Reading[], online: boolean, onProgress?: (open: number) => void
): Promise<{ points: Record<string, GeoPoint | null>; missingOffline: number }> {
  const queries = new Map<string, { street: string; city: string }>();
  for (const item of items) {
    const key = addressKey(item);
    if (key !== '|' && !queries.has(key)) queries.set(key, { street: (item.Strasse ?? '').trim(), city: (item.Ort ?? '').trim() });
  }
  const points: Record<string, GeoPoint | null> = {};
  const now = Date.now();
  const stored = new Map((await getGeoEntries([...queries.keys()])).map(entry => [entry.key, entry]));
  const toFetch: string[] = [];
  for (const key of queries.keys()) {
    const entry = stored.get(key);
    const found = entry?.lat !== undefined && entry.lon !== undefined;
    if (entry && now - entry.at < (found ? FOUND_MAX_AGE_MS : MISSING_MAX_AGE_MS)) points[key] = found ? { lat: entry.lat!, lon: entry.lon! } : null;
    else if (found) points[key] = { lat: entry!.lat!, lon: entry!.lon! }; // abgelaufen, aber besser als nichts, falls offline
    else toFetch.push(key);
  }
  if (!online) return { points, missingOffline: toFetch.length };
  for (let start = 0; start < toFetch.length; start += CHUNK) {
    const chunk = toFetch.slice(start, start + CHUNK);
    onProgress?.(toFetch.length - start);
    const results = await geocodeAddresses(chunk.map(key => queries.get(key)!));
    await putGeoEntries(chunk.map((key, index) => ({ key, lat: results[index]?.lat, lon: results[index]?.lon, at: Date.now() })));
    chunk.forEach((key, index) => { points[key] = results[index]; });
  }
  onProgress?.(0);
  return { points, missingOffline: 0 };
}
